import { useState, useEffect, useCallback } from "react";
import Lenis from "lenis";
import { LangProvider, useLang, ui, tx } from "./i18n";
import { Header } from "./components/Header";
import { TabNav } from "./components/TabNav";
import { Hero } from "./components/Hero";
import { EmergencyFab } from "./components/EmergencyFab";
import { ClimaTab } from "./components/tabs/ClimaTab";
import { DirectorioTab } from "./components/DirectorioTab";
import { ChecklistsTab } from "./components/ChecklistsTab";
import { GuideTab } from "./components/GuideTab";
import { PreguntasTab } from "./components/tabs/PreguntasTab";
import { SobreTab } from "./components/SobreTab";
import { MissionStrip } from "./components/MissionStrip";
import { AdminPanel } from "./components/AdminPanel";
import { GUIDES } from "./data/guides";

function Shell() {
  const { lang } = useLang();
  const [activeTab, setActiveTab] = useState("clima");
  const [pendingMsgs, setPendingMsgs] = useState(0);

  useEffect(() => {
    const check = () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/pending-messages`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setPendingMsgs(d.pending))
        .catch(() => {});
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, []);
  const [lowBandwidth, setLowBandwidth] = useState(() => localStorage.getItem("ppr-low-bw") === "1");
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem("ppr-high-contrast") === "1");

  const toggleHighContrast = useCallback(() => {
    setHighContrast((prev) => {
      const next = !prev;
      localStorage.setItem("ppr-high-contrast", next ? "1" : "0");
      return next;
    });
  }, []);

  const toggleLowBandwidth = useCallback(() => {
    setLowBandwidth((prev) => {
      const next = !prev;
      localStorage.setItem("ppr-low-bw", next ? "1" : "0");
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("low-bandwidth", lowBandwidth);
    document.documentElement.classList.toggle("high-contrast", highContrast);
    document.documentElement.lang = lang;
  }, [lowBandwidth, highContrast, lang]);

  useEffect(() => {
    if (activeTab === "admin") return;
    const key = `ppr-visit-${activeTab}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tab: activeTab }),
    }).catch(() => {});
  }, [activeTab]);

  useEffect(() => {
    if (lowBandwidth || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.11 });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [lowBandwidth]);

  const navigate = (tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const renderTab = () => {
    switch (activeTab) {
      case "clima":
        return (
          <>
            <Hero lowBandwidth={lowBandwidth} onNavigate={navigate} />
            <MissionStrip />
            <ClimaTab lowBandwidth={lowBandwidth} />
          </>
        );
      case "directorio":
        return <DirectorioTab lowBandwidth={lowBandwidth} />;
      case "checklists":
        return <ChecklistsTab lowBandwidth={lowBandwidth} />;
      case "preguntas":
        return <PreguntasTab />;
      case "sobre":
        return <SobreTab />;
      case "admin":
        return <AdminPanel />;
      default:
        return <GuideTab guide={GUIDES[activeTab]} lowBandwidth={lowBandwidth} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header lowBandwidth={lowBandwidth} onToggleLowBandwidth={toggleLowBandwidth} highContrast={highContrast} onToggleHighContrast={toggleHighContrast} />
      <TabNav active={activeTab} onChange={navigate} />
      <main>{renderTab()}</main>
      <footer className="no-print border-t border-slate-300 bg-[#1C2B4A] px-4 sm:px-8 lg:px-12 py-8 text-slate-300">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="font-display font-black text-white tracking-tight">
            preparatepr<span className="text-amber-500">.org</span>
          </p>
          <p className="text-sm">{tx(ui.footer.tagline, lang)}</p>
          <a data-testid="footer-email" href={`mailto:${ui.about.email}`} className="text-sm font-semibold text-slate-300 hover:text-amber-400 transition-colors duration-200">
            {ui.about.email}
          </a>
          <button
            data-testid="footer-admin-link"
            onClick={() => navigate("admin")}
            className="relative text-xs font-semibold text-slate-500 hover:text-slate-200 transition-colors duration-200"
          >
            Admin
            {pendingMsgs > 0 && (
              <span data-testid="admin-pending-dot" className="absolute -top-1.5 -right-3 flex h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse-dot" aria-label={`${pendingMsgs} mensajes pendientes`} />
            )}
          </button>
          <a href="tel:911" data-testid="footer-call-911" className="inline-flex items-center gap-2 rounded-md bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 transition-colors duration-200">
            {lang === "es" ? "Emergencias: 911" : "Emergencies: 911"}
          </a>
        </div>
      </footer>
      <EmergencyFab />
    </div>
  );
}

function App() {
  return (
    <LangProvider>
      <Shell />
    </LangProvider>
  );
}

export default App;
